import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useHistory, Redirect } from 'react-router-dom';

import { useQuery, useQueryClient, useMutation } from '@sopranodesign/pulse-hooks';
import { AnimateSharedLayout } from '@sopranodesign/framer-motion';
import {
  Tag,
  Flex,
  Span,
  Button,
  Heading2,
  useModal,
  ModalBody,
  TabButton,
  ModalFooter,
  ModalHeader,
  Placeholder,
  ModalContent,
  ModalContainer,
  useNotification
} from '@sopranodesign/pulse-components';
import { PermissionFilter, PermissionsFilter } from '@sopranodesign/pulse-auth';
import Icon from '@sopranodesign/pulse-icons';
import {
  getTicket,
  SubscriberDetails,
  ForwardTicketButton
} from '@sopranodesign/pulse-tickets';

import {
  claimTicket as claimTicketApi,
  updateTicket as updateTicketApi,
  archiveTicket as archiveTicketApi
} from '../../../../api/index';
import { Conversation, Ticket, ResolveButton } from './Components/index';

const REFRESH_INTERVAL = 8000;

const muteSoundsTemporarily = (duration = 5000) => {
  localStorage.setItem('liveChatSoundsMuted', 'true');
  setTimeout(() => {
    localStorage.removeItem('liveChatSoundsMuted');
  }, duration);
};

const showUnclaim = ticket => ticket.status === 'in_progress' && ticket.is_active;
const showForward = ticket => showUnclaim(ticket);
const showResolve = ticket => ticket.status === 'in_progress';
const showDelete = ticket => ticket.status === 'in_progress';
const showClaim = ticket => ticket.status !== 'in_progress' && ticket.status !== 'resolved';

const LiveChat = ({ match, agent }) => {
  const [tab, setTab] = useState('CONVERSATION');
  const [claimButton, setClaimButton] = useState(false);
  const [unclaimButton, setUnclaimButton] = useState(false);
  const { showSuccess } = useNotification();
  const { showModal, hideModal } = useModal();
  const history = useHistory();
  const queryClient = useQueryClient();

  const ticketQuery = useQuery({
    queryKey: [`tickets/ubidesk/${match.params.id}`, { tab }],
    gcTime: 0,
    refetchIntervalInBackground: REFRESH_INTERVAL,
    refetchInterval: REFRESH_INTERVAL
  });

  const claimTicket = useMutation({
    mutationFn: claimTicketApi,
    onSuccess: ({ data }) => {
      showSuccess('Successfully claimed live chat ticket.');
      queryClient.invalidateQueries({ queryKey: ['tickets/ubidesk'] });
      queryClient.invalidateQueries({ queryKey: [`tickets/ubidesk/${data.data.id}`] });
      setClaimButton(false);
    },
    onError: () => {
      setClaimButton(false);
    }
  });

  const archiveTicket = useMutation({
    mutationFn: archiveTicketApi,
    onSuccess: () => {
      showSuccess('Successfully archived live chat ticket.');
      queryClient.invalidateQueries({ queryKey: ['tickets/ubidesk'] });
      history.push(`/live-chat`);
    }
  });

  const unClaimTicket = useMutation({
    mutationFn: updateTicketApi,
    onSuccess: () => {
      showSuccess('Successfully unclaimed live chat ticket.');
      queryClient.invalidateQueries({ queryKey: ['tickets/ubidesk'] });
      history.push(`/live-chat`);
      setUnclaimButton(false);
    },
    onError: () => {
      setUnclaimButton(false);
    }
  });

  const handleClaim = () => {
    setClaimButton(true);
    muteSoundsTemporarily();
    claimTicket.mutate(match.params.id);
  };

  const handleDelete = () => {
    showModal({
      header: 'Remove ticket',
      body: `Are you sure you want to remove this live chat ticket?`,
      handleConfirm: async () => {
        try {
          await archiveTicket.mutate(ticketQuery.data.data.id);
        } finally {
          hideModal();
        }
      }
    });
  };

  const handleUnclaim = () => {
    setUnclaimButton(true);
    muteSoundsTemporarily();
    unClaimTicket.mutate({
      ...getTicket(ticketQuery.data.data),
      status: 'submitted',
      assignee_id: null
    });
  };

  const handleDeleteAfterSubscriberHasLeft = () => {
    archiveTicket.mutate(ticketQuery.data.data.id);
  };

  const canSeeLiveChat = useMemo(() => {
    if (!ticketQuery.isSuccess) {
      return false;
    }

    if (!ticketQuery.data.data.assignee) {
      return true;
    }

    return ticketQuery.data.data.assignee.id === agent.id;
  }, [ticketQuery, agent.id]);

  const onForward = async () => {
    await queryClient.invalidateQueries({ queryKey: ['tickets/ubidesk'] });
    history.push(`/live-chat`);
  };

  return (
    <Flex col fat style={{ height: '100vh' }}>
      {ticketQuery.isLoading && (
        <Flex pad fat>
          <Placeholder />
        </Flex>
      )}
      {ticketQuery.isSuccess && !canSeeLiveChat && (
        <Redirect
          to={{
            pathname: '/live-chat',
            state: {
              notification: {
                type: 'information',
                message: 'That ticket has already been claimed.'
              }
            }
          }}
        />
      )}
      {ticketQuery.isSuccess && canSeeLiveChat && (
        <>
          {/* Header */}
          <Flex col background="white" borderBottom pb>
            {!ticketQuery.data.data.is_active &&
              ticketQuery.data.data.status === 'in_progress' && (
                <ModalContainer>
                  <ModalContent>
                    <ModalHeader>Chatbot user left the conversation</ModalHeader>
                    <ModalBody>
                      The chatbot user manually ended the live chat. The ticket
                      can now either be deleted or resolved.
                    </ModalBody>
                    <ModalFooter>
                      <Flex fat xSpace right>
                        <PermissionFilter can="delete tickets">
                          <Button
                            variant="secondary"
                            icon="xCircle"
                            colour="danger"
                            onClick={handleDeleteAfterSubscriberHasLeft}>
                            Delete
                          </Button>
                        </PermissionFilter>
                        <PermissionFilter can="edit tickets">
                          <ResolveButton ticket={ticketQuery.data.data} />
                        </PermissionFilter>
                      </Flex>
                    </ModalFooter>
                  </ModalContent>
                </ModalContainer>
              )}
            <Flex pl pr pt col>
              <Flex fat between>
                <Flex mbSm xSpaceSm center>
                  <Icon width="32px" size="32px" height="32px" type="userCircle" />
                  <Heading2>
                    <Flex xSpace center>
                      <Span>User #{ticketQuery.data.data.subscriber.id}</Span>
                      {ticketQuery.data.data.status === 'in_progress' ? (
                        <Tag background_colour="positive" colour="positive">Claimed</Tag>
                      ) : ticketQuery.data.data.status === 'resolved' ? (
                        <Tag background_colour="positive" colour="positive">Resolved</Tag>
                      ) : (
                        <Tag background_colour="warning" colour="warning">Pending</Tag>
                      )}
                    </Flex>
                  </Heading2>
                </Flex>
                <Flex xSpace>
                  {showDelete(ticketQuery.data.data) && (
                    <PermissionFilter can="delete tickets">
                      <Button
                        variant="secondary"
                        icon="xCircle"
                        colour="danger"
                        onClick={handleDelete}>
                        Delete
                      </Button>
                    </PermissionFilter>
                  )}
                  {showClaim(ticketQuery.data.data) && (
                    <PermissionFilter can="edit tickets">
                      <Button
                        variant="primary"
                        icon="plus"
                        onClick={handleClaim}
                        disabled={claimButton}
                      >
                        Claim
                      </Button>
                    </PermissionFilter>
                  )}
                  {showUnclaim(ticketQuery.data.data) && (
                    <PermissionFilter can="edit tickets">
                      <Button
                        variant="secondary"
                        icon="xCircle"
                        colour="danger"
                        onClick={handleUnclaim}
                        disabled={unclaimButton}
                      >
                        Unclaim
                      </Button>
                    </PermissionFilter>
                  )}
                  {showForward(ticketQuery.data.data) && (
                    <PermissionsFilter can={['edit tickets', 'view agents']}>
                      <ForwardTicketButton
                        id={ticketQuery.data.data.id}
                        onSuccess={onForward}>
                        Forward
                      </ForwardTicketButton>
                    </PermissionsFilter>
                  )}
                  {showResolve(ticketQuery.data.data) && (
                    <PermissionFilter can="edit tickets">
                      <ResolveButton ticket={ticketQuery.data.data} />
                    </PermissionFilter>
                  )}
                </Flex>
              </Flex>
              <Flex center xSpace>
                <AnimateSharedLayout>
                  <TabButton active={tab === 'CONVERSATION'} onClick={() => setTab('CONVERSATION')}>Conversation</TabButton>
                  <TabButton active={tab === 'SUBSCRIBER'} onClick={() => setTab('SUBSCRIBER')}>User</TabButton>
                  <TabButton active={tab === 'TICKET'} onClick={() => setTab('TICKET')}>Ticket</TabButton>
                </AnimateSharedLayout>
              </Flex>
            </Flex>
          </Flex>

          {/* Tab Content */}
          <Flex col fat style={{ flexGrow: 1, minHeight: 0 }}>
            {tab === 'CONVERSATION' && <Conversation ticket={ticketQuery.data.data} />}
            {tab === 'TICKET' && <Ticket ticket={ticketQuery.data.data} />}
            {tab === 'SUBSCRIBER' && (
              <Flex pad fat tall>
                <SubscriberDetails subscriber={ticketQuery.data.data.subscriber} ticket={ticketQuery.data.data} />
              </Flex>
            )}
          </Flex>
        </>
      )}
    </Flex>
  );
};

LiveChat.propTypes = {
  agent: PropTypes.shape({
    id: PropTypes.number.isRequired
  }).isRequired
};

export default LiveChat;